import React, { useEffect, useState } from "react";
import Loader from "../../../components/Loader";
import { Box, Grid2, TextField } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import CustomSwitch from "../../../components/CustomSwitch/CustomSwitch";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useNavigationGuard from "../../../hooks/useNavigationGuard";
import {
  addNewRoleService,
  getAllLandingPageService,
  getRoleByIdService,
  updateRoleService,
} from "../../../services/roleManagementService";
import { notifyError, notifySuccess } from "../../../utilities/helper";
import { CreateHandleUnsaveNavigation } from "../../../utilities/handleUnsaveNavigation";
import SearchableSelect from "../../../components/SearchableSelect";

const AddRoleManagement = () => {
  const [load, setLoad] = useState(false);
  const [landingPagesData, setLandingPagedData] = useState([]);

  const navigate = useNavigate();
  const id = useLocation().state?.id;

  const access_token = localStorage.getItem("access_token");
  const handleUnsaveNavigation = CreateHandleUnsaveNavigation();

  const createRoleSchema = yup.object().shape({
    name: yup
      .string()
      .required("Name is required")
      .matches(
        /^[A-Za-z][A-Za-z0-9\s!@#$%^&*()_+=-]*$/,
        "Name must not start with a number or special character"
      )
      .test("has-letter", "Name must contain at least one alphabet", (value) =>
        /[A-Za-z]/.test(value || "")
      ),
    notes: yup.string().required("Notes is required"),
    description: yup.string().required("Description is required"),
    landingPage: yup.string().required("Landing Page is required"),
    isBlinded: yup.boolean(),
    autoAssign: yup.boolean(),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(createRoleSchema),
    defaultValues: {
      name: "",
      notes: "",
      description: "",
      landingPage: "",
      isBlinded: false,
      autoAssign: false,
    },
  });

  useNavigationGuard(isDirty);

  useEffect(() => {
    getLandingPagesData();
  }, []);

  useEffect(() => {
    if (id && landingPagesData && landingPagesData?.length > 0) {
      getRoleById(id);
    }
  }, [id, landingPagesData]);

  const createUpdateRole = async (data) => {
    setLoad(true);
    let newData = {
      shortName: data?.name,
      notes: data?.notes,
      description: data?.description,
      isBlinded: data?.isBlinded,
      autoAssignNewSites: data?.autoAssign,
      landingPageUrl: data?.landingPage,
      landingPageControllerName: "",
      landingPageControllerActionName: "",
    };
    if (!id) {
      try {
        const res = await addNewRoleService(newData);
        if (res?.status == 200) {
          setLoad(false);
          notifySuccess("Role added successfully");
          navigate("/role-management");
        }
      } catch (err) {
        setLoad(false);
        console.log(err);
        notifyError(err?.message);
        if (err.status === 401) {
          navigate("/unauthorize");
        }
      }
    } else {
      try {
        const res = await updateRoleService(id, newData, access_token);
        if (res?.status == 200) {
          setLoad(false);
          notifySuccess("Role updated successfully");
          navigate("/role-management");
        }
      } catch (err) {
        setLoad(false);
        console.log(err.message);
        notifyError(err.message);
        if (err.status === 401) {
          navigate("/unauthorize");
        }
      }
    }
  };

  const getLandingPagesData = async () => {
    setLoad(true);
    try {
      const res = await getAllLandingPageService(access_token);
      if (res?.status == 200) {
        if (res?.data?.result?.length > 0) {
          setLoad(false);
          setLandingPagedData(res?.data?.result);
        } else {
          setLoad(false);
          setLandingPagedData([]);
        }
      }
    } catch (err) {
      setLoad(false);
      console.log(err);
      if (err.status === 401) {
        navigate("/unauthorize");
      }
    }
  };

  const getRoleById = async (roleId) => {
    setLoad(true);
    try {
      const res = await getRoleByIdService(roleId, access_token);
      if (res?.status == 200) {
        setLoad(false);
        if (res?.data?.result) {
          reset({
            name: res?.data?.result?.shortName,
            notes: res?.data?.result?.notes,
            description: res?.data?.result?.description,
            landingPage:
              (landingPagesData &&
                landingPagesData?.find(
                  (item) => item?.key == res?.data?.result?.landingPageUrl
                )?.key) ||
              "",
            autoAssign: res?.data?.result?.autoAssignNewSites,
            isBlinded: res?.data?.result?.isBlinded,
          });
          // setIsBlinded(res?.data?.result?.isBlinded);
          // setAutoAssign(res?.data?.result?.autoAssignNewSites);
        }
      }
    } catch (err) {
      setLoad(false);
      console.log(err?.message);
      notifyError(err.message);
      if (err.status === 401) {
        navigate("/unauthorize");
      }
    }
  };

  return (
    <>
      {load ? (
        <Loader />
      ) : (
        <div className="content-body">
          <p className="bread-crumbs-link-container">
            <button
              className="bread-crumbs-btn"
              onClick={(e) => handleUnsaveNavigation(e, isDirty, "", navigate)}
            >
              Home
            </button>{" "}
            |{" "}
            <button
              className="bread-crumbs-btn"
              onClick={(e) =>
                handleUnsaveNavigation(e, isDirty, "role-management", navigate)
              }
            >
              Role Management
            </button>{" "}
            |{" "}
            <button className="bread-crumbs-btn">
              {id ? "Edit Role Management" : "Add Role Management"}
            </button>
            -{" "}
            <b style={{ color: "gray" }}>
              {localStorage?.getItem("studyName")}
            </b>
          </p>
          <Grid2
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%", margin: "0 auto" }}
          >
            <Grid2 item md={12}>
              <p className="page-heading">Role Details</p>
            </Grid2>
          </Grid2>

          <Box
            component="form"
            autoComplete="off"
            sx={{ height: "auto", width: "100%" }}
            onSubmit={handleSubmit(createUpdateRole)}
          >
            <Grid2
              container
              alignItems="center"
              justifyContent={"space-between"}
              sx={{ width: "100%", margin: "0 auto" }}
              gap={1}
            >
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Name
                    <span>*</span>
                  </p>
                  <TextField
                    size="small"
                    fullWidth
                    type={"text"}
                    {...register("name")}
                  />
                </div>
                {errors.name && (
                  <div className="error-container">{errors.name.message}</div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Notes
                    <span>*</span>
                  </p>
                  <TextField
                    fullWidth
                    size="small"
                    type={"text"}
                    {...register("notes")}
                  />
                </div>
                {errors.notes && (
                  <div className="error-container">{errors.notes.message}</div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Description
                    <span>*</span>
                  </p>
                  <TextField
                    fullWidth
                    size="small"
                    type={"text"}
                    {...register("description")}
                  />
                </div>
                {errors.description && (
                  <div className="error-container">
                    {errors.description.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Landing Page
                    <span>*</span>
                  </p>
                  {/* <Controller
                    name="landingPage"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Select
                        id="landing-page"
                        fullWidth
                        size="small"
                        displayEmpty
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        inputProps={{ "aria-label": "Without label" }}
                        // disabled
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {landingPagesData &&
                          landingPagesData.map((item, index) => (
                            <MenuItem key={index} value={item?.key}>
                              {item?.value}
                            </MenuItem>
                          ))}
                      </Select>
                    )}
                  /> */}
                  <SearchableSelect
                    control={control}
                    data={landingPagesData}
                    name={"landingPage"}
                  />
                </div>
                {errors.landingPage && (
                  <div className="error-container">
                    {errors.landingPage.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ lg: 12, sm: 12, xl: 12, md: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">Is Blinded</p>
                  <Controller
                    name="isBlinded"
                    control={control}
                    defaultValue={false} // or true if that's your initial value
                    render={({ field }) => (
                      <CustomSwitch
                        isSwitch={field.value}
                        setSwitch={(val) => field.onChange(val)}
                      />
                    )}
                  />
                </div>
              </Grid2>
              <Grid2 size={{ lg: 12, sm: 12, xl: 12, md: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">Auto Assign New Sites</p>
                  <Controller
                    name="autoAssign"
                    control={control}
                    defaultValue={false} // or true, depending on your form's initial value
                    render={({ field }) => (
                      <CustomSwitch
                        isSwitch={field.value}
                        setSwitch={(val) => field.onChange(val)}
                      />
                    )}
                  />
                </div>
              </Grid2>
            </Grid2>

            <Grid2
              alignItems={"end"}
              justifyContent="end"
              container
              gap={3}
              sx={{ marginTop: "2%" }}
            >
              <Grid2 item md={6}>
                <button
                  type="button"
                  className="create-btn-outlined"
                  onClick={(e) => {
                    e.preventDefault();
                    handleUnsaveNavigation(
                      e,
                      isDirty,
                      "role-management",
                      navigate
                    );
                  }}
                >
                  Cancel
                </button>
              </Grid2>
              <Grid2 item md={6}>
                <button type="submit" className="create-btn">
                  {id ? "Update" : "Save"}
                </button>
              </Grid2>
            </Grid2>
          </Box>
        </div>
      )}
    </>
  );
};

export default AddRoleManagement;
